import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { QUERY_KEY } from '~common';
import { postRequest } from '~services';
import {
  GetVenueExistenceStatusRequest,
  GetVenueExistenceStatusResponse,
  VenueExistenceStatusAssociateByGooglePlaceId,
} from '~types';

interface useQueryGetVenueExistenceStatusProps<T> {
  request: GetVenueExistenceStatusRequest;
  selectFn?: (data: GetVenueExistenceStatusResponse) => T;
  options?: {
    enabled?: boolean;
  };
}

async function fetchVenueExistenceStatus(
  request: GetVenueExistenceStatusRequest,
): Promise<GetVenueExistenceStatusResponse> {
  const res = await postRequest<GetVenueExistenceStatusRequest>({
    path: 'api/admin/venue/list-by-google-place-ids',
    data: request,
  });
  return res.data;
}

function transformToVenueExistenceStatusAssociateByGooglePlaceId(
  data: GetVenueExistenceStatusResponse,
) {
  return data.data.reduce<VenueExistenceStatusAssociateByGooglePlaceId>(
    (acc, cur) => {
      acc.set(cur.googlePlaceId, cur);
      return acc;
    },
    new Map(),
  );
}

export function useQueryGetVenueExistenceStatus<T>({
  request,
  selectFn,
  options,
}: useQueryGetVenueExistenceStatusProps<T>): UseQueryResult<T> {
  return useQuery({
    queryKey: [QUERY_KEY.GET_VENUES_BY_GOOGLE_PLACE_IDS, request],
    queryFn: () => fetchVenueExistenceStatus(request),
    enabled: options?.enabled ?? true,
    select: selectFn,
  });
}

export function useVenueExistenceStatusAssociateByGooglePlaceId({
  request,
  options,
}: useQueryGetVenueExistenceStatusProps<VenueExistenceStatusAssociateByGooglePlaceId>) {
  return useQueryGetVenueExistenceStatus<VenueExistenceStatusAssociateByGooglePlaceId>(
    {
      request,
      selectFn: (data) => {
        return transformToVenueExistenceStatusAssociateByGooglePlaceId(data);
      },
      options,
    },
  );
}
